import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { PhoneIcon, UserIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import { motion } from 'framer-motion';
import { ClipLoader } from 'react-spinners';
import { useSnackbar } from '../contexts/SnackbarContext';

interface BookingModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

export default function BookingModal({ isOpen, closeModal }: BookingModalProps) {
  const [fullName, setFullName] = useState('');
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [phone, setPhone] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [service, setService] = useState('Accommodation');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { showSnackbar } = useSnackbar();

  const services = [
    'Accommodation', 
    'Guided Trek', 
    'Local Experience', 
    'Cultural Tour', 
    'Family Stay'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Format dates as "21 June 2025" in Asia/Kathmandu timezone
    const formatDate = (date: Date | null): string => {
      if (!date) return '';
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Asia/Kathmandu'
      });
    };

    const bookingData = {
      fullName,
      checkInDate: formatDate(checkInDate),
      checkOutDate: formatDate(checkOutDate),
      phone,
      adults,
      children,
      service,
      message
    };
    try {
      // Option 1: Keep relative path (recommended for Netlify)
      const res = await fetch('/.netlify/functions/sendBookingEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });
      
      let responseData;
      try {
        responseData = await res.json();
      } catch (parseError) {
        responseData = { error: 'Invalid response format' };
      }
      
      if (res.ok) {
        showSnackbar('Booking request sent successfully!', 'success');
        // Reset form
        setFullName('');
        setCheckInDate(null);
        setCheckOutDate(null);
        setPhone('');
        setAdults(2);
        setChildren(0);
        setService('Accommodation');
        setMessage('');
        setTimeout(() => closeModal(), 1000);
      } else {
        console.error('Booking submission failed:', responseData);
        showSnackbar(responseData.error || 'Failed to send booking request', 'error');
      }
    } catch (err: any) {
      console.error('Network error:', err);
      showSnackbar('Network error - please check your connection', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-4 md:p-6 text-left align-middle shadow-xl transition-all">
                <motion.div 
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute top-3 right-3"
                >
                  <button
                    type="button"
                    className="rounded-md text-neutral-500 hover:text-neutral-800 focus:outline-none"
                    onClick={closeModal}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Dialog.Title
                    as="h3"
                    className="text-xl md:text-2xl font-medium leading-6 text-primary-700 mb-2"
                  >
                    Book Your Stay
                  </Dialog.Title>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Full Name */}
                      <div className="md:col-span-2">
                        <label htmlFor="fullName" className="block text-xs font-normal text-neutral-700 mb-1">
                          Full Name *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <UserIcon className="h-4 w-4 text-primary-600" />
                          </div>
                          <input
                            type="text"
                            id="fullName"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="pl-8 block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                            placeholder="Enter your full name"
                            required
                          />
                        </div>
                      </div>

                      {/* Check-in Date */}
                      <div className="relative">
                        <label htmlFor="check-in" className="block text-xs font-normal text-neutral-700 mb-1">
                          Check-in Date *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <CalendarIcon className="h-4 w-4 text-primary-600" />
                          </div>
                          <DatePicker
                            id="check-in"
                            selected={checkInDate}
                            onChange={(date) => setCheckInDate(date)}
                            selectsStart
                            startDate={checkInDate}
                            endDate={checkOutDate}
                            minDate={new Date()}
                            placeholderText="Select date"
                            className="pl-8 block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                            required
                          />
                        </div>
                      </div>

                      {/* Check-out Date */}
                      <div className="relative">
                        <label htmlFor="check-out" className="block text-xs font-normal text-neutral-700 mb-1">
                          Check-out Date *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <CalendarIcon className="h-4 w-4 text-primary-600" />
                          </div>
                          <DatePicker
                            id="check-out"
                            selected={checkOutDate}
                            onChange={(date) => setCheckOutDate(date)}
                            selectsEnd
                            startDate={checkInDate}
                            endDate={checkOutDate}
                            minDate={checkInDate || new Date()}
                            placeholderText="Select date"
                            className="pl-8 block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                            required
                          />
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div>
                        <label htmlFor="phone" className="block text-xs font-normal text-neutral-700 mb-1">
                          Phone Number *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <PhoneIcon className="h-4 w-4 text-primary-600" />
                          </div>
                          <input
                            type="tel"
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="pl-8 block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                            placeholder="+977-98xxxxxxxx"
                            required
                          />
                        </div>
                      </div>

                      {/* Service Type */}
                      <div>
                        <label htmlFor="service" className="block text-xs font-normal text-neutral-700 mb-1">
                          Service Type *
                        </label>
                        <select
                          id="service"
                          value={service}
                          onChange={(e) => setService(e.target.value)}
                          className="block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                          required
                        >
                          {services.map((svc) => (
                            <option key={svc} value={svc}>{svc}</option>
                          ))}
                        </select>
                      </div>

                      {/* Number of Adults */}
                      <div className="relative">
                        <label htmlFor="adults" className="block text-xs font-normal text-neutral-700 mb-1">
                          Number of Adults *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <UserIcon className="h-4 w-4 text-primary-600" />
                          </div>
                          <select
                            id="adults"
                            value={adults}
                            onChange={(e) => setAdults(Number(e.target.value))}
                            className="pl-8 block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                            required
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                              <option key={num} value={num}>{num}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Number of Children */}
                      <div className="relative">
                        <label htmlFor="children" className="block text-xs font-normal text-neutral-700 mb-1">
                          Number of Children
                        </label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <UserGroupIcon className="h-4 w-4 text-primary-600" />
                          </div>
                          <select
                            id="children"
                            value={children}
                            onChange={(e) => setChildren(Number(e.target.value))}
                            className="pl-8 block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2"
                          >
                            {[0, 1, 2, 3, 4, 5, 6].map((num) => (
                              <option key={num} value={num}>{num}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Message section */}
                    <div className="mt-2">
                      <label htmlFor="message" className="block text-xs font-normal text-neutral-700 mb-1">
                        Message (optional)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="block w-full text-xs rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 resize-none py-2"
                        placeholder="Add any special requests or questions here..."
                      />
                    </div>

                    {/* Submit Button */}
                    <motion.div 
                      className="mt-6"
                      whileHover={{ scale: isLoading ? 1 : 1.02 }}
                      whileTap={{ scale: isLoading ? 1 : 0.98 }}
                    >
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-normal text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <>
                            <ClipLoader color="white" size={16} className="mr-2" />
                            Processing...
                          </>
                        ) : (
                          'Book Request'
                        )}
                      </button>
                    </motion.div>
                  </form>
                </motion.div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
